import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { EmptyState, ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { cancelSlot, createSlot, fetchMySlots } from '@/features/availability/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime } from '@/lib/datetime';

/**
 * サクセス担当の「実施可能日」（D-183）.
 *
 * ここで登録した日時が、日時未定のサクセス面談（事務局が作成 / D-173）の
 * 候補日として受講生に表示され、受講生が選ぶと面談の日時が確定する。
 * 旧・枠予約方式（枠を選ぶとイベントが自動生成される）とは違い、
 * **枠そのものは面談に紐づかない**（自分の空き時間をまとめて登録しておく置き場）。
 */
export function AvailabilityPage(): ReactElement {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['availability-slots'], queryFn: fetchMySlots });

  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user?.active_role !== 'success') {
    return (
      <>
        <PageHeader title="実施可能日" />
        <ForbiddenState />
      </>
    );
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await createSlot({
        starts_at: datetimeLocalToIso(startsAt),
        ends_at: datetimeLocalToIso(endsAt),
      });
      await queryClient.invalidateQueries({ queryKey: ['availability-slots'] });
      setStartsAt('');
      setEndsAt('');
      setNotice('実施可能日を登録しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(slotId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await cancelSlot(slotId);
      await queryClient.invalidateQueries({ queryKey: ['availability-slots'] });
      setNotice('実施可能日を取り消しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '取消に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const now = Date.now();

  return (
    <>
      <PageHeader
        title="実施可能日"
        description="面談を実施できる日時を登録しておく画面です。事務局がサクセス面談を作成すると、対象の受講生にここで登録した日時が候補として表示され、受講生が選ぶと面談の日時が確定します（確定するとその面談のグループへ自動でお知らせが入ります）。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}

      <MetaBox title="実施可能日を登録する">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">
              開始<span className="text-wpnotice-error"> *</span>
            </span>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">
              終了<span className="text-wpnotice-error"> *</span>
            </span>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <button
            type="button"
            disabled={busy || !startsAt || !endsAt}
            onClick={() => void onCreate()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            登録する
          </button>
        </div>
      </MetaBox>

      <MetaBox title="登録済みの実施可能日">
        {query.isPending ? (
          <SkeletonRows rows={4} columns={3} />
        ) : query.isError ? (
          <ErrorState message="実施可能日を読み込めませんでした。" onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState
            title="実施可能日はまだ登録されていません"
            description="上のフォームから、面談を実施できる日時を登録してください。"
          />
        ) : (
          <ul className="divide-y divide-wpborder">
            {query.data.map((slot) => {
              const past = new Date(slot.starts_at).getTime() < now;
              return (
                <li key={slot.id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className={`tabular-nums ${past ? 'text-wptext-muted' : ''}`}>
                    {formatDateTime(slot.starts_at)} 〜{' '}
                    {new Date(slot.ends_at).toLocaleTimeString('ja-JP', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {slot.claimed_at ? (
                    <span className="rounded-sm bg-wpnotice-success px-2 py-0.5 text-xs font-semibold text-white">
                      選択済み（面談が確定しています）
                    </span>
                  ) : past ? (
                    <span className="text-wptext-muted">終了</span>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onCancel(slot.id)}
                      className="text-wpnotice-error hover:underline disabled:opacity-50"
                    >
                      取り消す
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </MetaBox>
    </>
  );
}
